import { Box, FormControl, InputAdornment, InputLabel, Select, SelectChangeEvent, TextField } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Dispatch, FC, SetStateAction } from 'react';
import { DevicesLocationSelect, DevicesSearch, devicesTypes, DevicesTypeSelect } from '@pages/booking/types';
import { selectBookingDevicesLocations, selectGetBookingDevicesLocationsStatus } from '@pages/booking/store/selectors';
import useDebouncedInputValue from '@shared/hooks/useDebouncedInputValue';
import { RequestStatusEnum } from '@shared/types';
import { MAP_DEVICE_TYPE_TO_LABEL } from '@pages/booking/constants';
import IconButton from '@mui/material/IconButton';
import ClearIcon from '@mui/icons-material/Clear';

interface ClearInputAdornmentProps {
    onClick: () => void;
}
const ClearInputAdornment: FC<ClearInputAdornmentProps> = ({ onClick }) => (
    <InputAdornment position="start">
        <IconButton onClick={onClick} size="small">
            <ClearIcon />
        </IconButton>
    </InputAdornment>
);

export type BookingCalendarToolbarFiltersProps = {
    devicesSearch: DevicesSearch;
    setDevicesSearch: Dispatch<SetStateAction<DevicesSearch>>;
    devicesLocation: DevicesLocationSelect;
    setDevicesLocation: Dispatch<SetStateAction<DevicesLocationSelect>>;
    devicesType: DevicesTypeSelect;
    setDevicesType: Dispatch<SetStateAction<DevicesTypeSelect>>;
};

const BookingCalendarToolbarFilters: FC<BookingCalendarToolbarFiltersProps> = ({
    devicesSearch,
    setDevicesSearch,
    devicesLocation,
    setDevicesLocation,
    devicesType,
    setDevicesType,
}) => {
    const { t } = useTranslation();
    const locations = useSelector(selectBookingDevicesLocations);
    const getLocationsStatus = useSelector(selectGetBookingDevicesLocationsStatus);
    const [devicesSearchLocalValue, handleChangeDevicesSearch, handleClearDevicesSearch] = useDebouncedInputValue({
        state: devicesSearch,
        setState: setDevicesSearch,
        wait: 500,
    });

    const handleChangeDevicesLocation = (event: SelectChangeEvent) => {
        setDevicesLocation(event.target.value);
    };

    const handleClearDevicesLocation = () => {
        setDevicesLocation('');
    };

    const handleChangeDevicesType = (event: SelectChangeEvent) => {
        setDevicesType(event.target.value as DevicesTypeSelect);
    };

    const handleClearDevicesType = () => {
        setDevicesType('');
    };

    return (
        <Box sx={{ flex: 1, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <TextField
                sx={{ minWidth: 300, maxWidth: 700, flex: 1 }}
                size="small"
                label={`${t('search')}...`}
                value={devicesSearchLocalValue}
                onChange={handleChangeDevicesSearch}
                slotProps={{
                    input: {
                        endAdornment: devicesSearchLocalValue && (
                            <ClearInputAdornment onClick={handleClearDevicesSearch} />
                        ),
                    },
                }}
            />
            <FormControl sx={{ maxWidth: 320, minWidth: 160, flex: 1 }} size="small">
                <InputLabel id="location-select-label">{t('location')}</InputLabel>
                <Select
                    labelId="location-select-label"
                    value={devicesLocation}
                    label={t('location')}
                    onChange={handleChangeDevicesLocation}
                    endAdornment={devicesLocation && <ClearInputAdornment onClick={handleClearDevicesLocation} />}
                >
                    {getLocationsStatus === RequestStatusEnum.Success &&
                        locations.map((location) => (
                            <MenuItem key={location.name} value={location.name}>
                                {location.name}
                            </MenuItem>
                        ))}
                    {getLocationsStatus === RequestStatusEnum.Loading && (
                        <Skeleton sx={{ margin: '0 auto', width: '90%' }} animation="wave" />
                    )}
                </Select>
            </FormControl>
            <FormControl sx={{ maxWidth: 320, minWidth: 160, flex: 1 }} size="small">
                <InputLabel id="type-select-label">{t('type')}</InputLabel>
                <Select
                    labelId="type-select-label"
                    value={devicesType}
                    label={t('type')}
                    onChange={handleChangeDevicesType}
                    endAdornment={devicesType && <ClearInputAdornment onClick={handleClearDevicesType} />}
                >
                    {devicesTypes.map((deviceType) => (
                        <MenuItem key={deviceType} value={deviceType}>
                            {MAP_DEVICE_TYPE_TO_LABEL[deviceType]}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </Box>
    );
};

export default BookingCalendarToolbarFilters;
